import React from 'react';

const PortfolioHedging = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Protective Puts",
      icon: "🛡️",
      content: {
        keyPoints: [
          "Buy puts to protect long positions",
          "Limits downside while keeping upside",
          "Cost of protection = put premium",
          "Insurance strategy for uncertain markets"
        ],
        example: "Own 10 BTC at $50k each. Buy $45k puts for $2k each to limit loss to $7k per BTC."
      },
      quiz: [
        {
          id: 'q1',
          question: "What's the main purpose of protective puts?",
          options: ["Increase profits", "Limit downside risk", "Eliminate all risk", "Generate income"],
          correct: 1,
          explanation: "Correct! Protective puts limit downside risk while allowing participation in upside moves."
        },
        {
          id: 'q2',
          question: "The cost of protective puts is:",
          options: ["Free protection", "The put premium", "Strike price", "Underlying value"],
          correct: 1,
          explanation: "Correct! You pay the put premium as the cost of downside protection."
        }
      ]
    },
    2: {
      title: "Collar Strategies",
      icon: "🎯",
      content: {
        keyPoints: [
          "Combine protective puts with covered calls",
          "Sell calls to finance put purchases",
          "Caps both upside and downside",
          "Zero-cost or low-cost protection possible"
        ],
        example: "Own BTC at $50k. Buy $45k put for $2k, sell $55k call for $2k. Net cost = $0."
      },
      quiz: [
        {
          id: 'q1',
          question: "A collar strategy involves:",
          options: ["Only buying puts", "Only selling calls", "Buying puts and selling calls", "Buying calls and puts"],
          correct: 2,
          explanation: "Correct! Collars combine buying protective puts with selling covered calls."
        },
        {
          id: 'q2',
          question: "How does selling calls help in a collar?",
          options: ["Increases upside", "Generates premium to offset put cost", "Eliminates risk", "Improves liquidity"],
          correct: 1,
          explanation: "Correct! Call premium helps offset the cost of buying protective puts."
        }
      ]
    },
    3: {
      title: "Index Hedging",
      icon: "📊",
      content: {
        keyPoints: [
          "Use index options to hedge portfolio exposure",
          "More efficient than individual stock hedges",
          "Protects against systematic risk",
          "Requires understanding portfolio beta"
        ],
        example: "Portfolio of crypto assets. Buy puts on crypto index to hedge against market-wide decline."
      },
      quiz: [
        {
          id: 'q1',
          question: "Index hedging is most effective against:",
          options: ["Individual asset risk", "Systematic market risk", "Currency risk", "Interest rate risk"],
          correct: 1,
          explanation: "Correct! Index hedging protects against systematic or market-wide risk that affects all assets."
        },
        {
          id: 'q2',
          question: "What's important to know for index hedging?",
          options: ["Individual stock prices", "Portfolio beta to index", "Interest rates", "Currency rates"],
          correct: 1,
          explanation: "Correct! Portfolio beta determines how much the portfolio moves relative to the index."
        }
      ]
    },
    4: {
      title: "Dynamic Hedging",
      icon: "🔄",
      content: {
        keyPoints: [
          "Adjust hedge ratios as market conditions change",
          "Rebalance based on delta and portfolio exposure",
          "Monitor correlation changes",
          "Active management required"
        ],
        example: "As BTC correlation to portfolio changes from 0.8 to 0.6, adjust hedge ratio accordingly."
      },
      quiz: [
        {
          id: 'q1',
          question: "Dynamic hedging requires:",
          options: ["Set and forget approach", "Active monitoring and adjustment", "Only initial setup", "High leverage"],
          correct: 1,
          explanation: "Correct! Dynamic hedging requires continuous monitoring and adjustments as conditions change."
        },
        {
          id: 'q2',
          question: "What triggers hedge ratio adjustments?",
          options: ["Time passage only", "Changes in correlations and exposures", "Broker recommendations", "Interest rate changes"],
          correct: 1,
          explanation: "Correct! Changes in correlations and portfolio exposures require hedge ratio adjustments."
        }
      ]
    },
    5: {
      title: "Portfolio Insurance Techniques",
      icon: "🛡️",
      content: {
        keyPoints: [
          "CPPI: Constant Proportion Portfolio Insurance",
          "Stop-loss strategies vs options-based insurance",
          "Floor protection with upside participation",
          "Cost-benefit analysis of insurance strategies"
        ],
        example: "CPPI strategy: Allocate more to risky assets when portfolio above floor, reduce when approaching floor."
      },
      quiz: [
        {
          id: 'q1',
          question: "CPPI stands for:",
          options: ["Cost Protection Portfolio Investment", "Constant Proportion Portfolio Insurance", "Covered Put Portfolio Integration", "Conservative Portfolio Protection Index"],
          correct: 1,
          explanation: "Correct! CPPI is Constant Proportion Portfolio Insurance, a dynamic hedging strategy."
        },
        {
          id: 'q2',
          question: "Portfolio insurance aims to:",
          options: ["Maximize returns", "Provide floor protection with upside", "Eliminate all volatility", "Generate income"],
          correct: 1,
          explanation: "Correct! Portfolio insurance provides downside protection while allowing upside participation."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced portfolio hedging strategies</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-green-500 pl-4">
            <h4 className="text-white font-bold mb-2">🛡️ Key Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-lg p-4">
            <h5 className="text-emerald-400 font-bold mb-2">📊 Hedging Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Hedging Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these concepts to hedge portfolios effectively</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-green-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `portfolio_hedging_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Portfolio Hedger'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'portfolio_hedging'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Portfolio Hedging Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default PortfolioHedging;
